import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import {
    Box,
    Button,
    Input,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Switch,
    FormControlLabel,
    Typography,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Checkbox,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    OutlinedInput,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    RadioGroup,
    Radio,
    FormLabel,
} from "@mui/material";
import HighlightOff from "@mui/icons-material/HighlightOff";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { pink, green, orange, blue } from "@mui/material/colors";
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import RecommendIcon from '@mui/icons-material/Recommend';
import InfoIcon from '@mui/icons-material/Info';
import { API_URL } from "../../consts";

// Dialog styling
const dialogStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "800px",
    maxHeight: "90vh",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 5,
    p: 4,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
};

const tabPanelStyle = {
    height: "100%",
    overflowY: "auto",
    p: 2,
};

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={tabPanelStyle}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const PERMISSIONS = {
    VIEW_ONLY: 'view_only',
    ADD_ITEMS: 'add_items',
    MANAGE_COLLECTIONS: 'manage_collections'
};

const PERMISSION_CONFIGS = {
    [PERMISSIONS.VIEW_ONLY]: {
        label: 'View Only',
        description: 'Can view items and collections in read-only mode',
        color: 'primary',
        exclusive: true
    },
    [PERMISSIONS.ADD_ITEMS]: {
        label: 'Add Items to Collections',
        description: 'Can add and edit items in existing collections',
        color: 'success'
    },
    [PERMISSIONS.MANAGE_COLLECTIONS]: {
        label: 'Manage Collections',
        description: 'Can create, edit and manage collections',
        color: 'info'
    }
};

const COLLABORATOR_STATUS = {
    PENDING: 'pending',
    ACCEPTED: 'accepted'
};

const STATUS_CONFIGS = {
    [COLLABORATOR_STATUS.PENDING]: {
        label: 'Pending',
        icon: <PendingIcon />,
        color: 'warning'
    },
    [COLLABORATOR_STATUS.ACCEPTED]: {
        label: 'Accepted',
        icon: <CheckCircleIcon />,
        color: 'success'
    }
};

/**
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onAddCollaboratorClick - Callback when adding a new collaborator
 * @param {Function} props.onUpdateCollaboratorPermissions - Callback to update collaborator permissions
 * @param {Array} props.collaborators - List of collaborators in the space
 * @param {Function} props.onDeleteCollaboratorClick - Callback to remove a collaborator
 * @param {Function} props.onDeleteSpaceClick - Callback to delete the entire space
 * @param {boolean} [props.isSpaceEmpty=true] - Whether the space is empty (enables "share with community" switch)
 * @param {Array} [props.subSpaces=[]] - List of sub-spaces in the space
 * @param {Array} [props.userPermissions=[]] - List of user permissions
 * @param {Function} props.getSpace - Callback to refresh the space data
 */
const SettingsDialog = ({
    spaceData,
    onChangeSpace,
    open,
    onClose,
    onAddCollaboratorClick,
    onUpdateCollaboratorPermissions,
    collaborators,
    onDeleteCollaboratorClick,
    onDeleteSpaceClick,
    isSpaceEmpty = true,
    subSpaces = [],
    userPermissions = [],
    getSpace
}) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    // Initialize tab value based on ownership - non-owners should start on Collaborators tab (index 0)
    const [tabValue, setTabValue] = useState(0);
    
    // Reset to appropriate tab when dialog opens/closes or permissions change
    useEffect(() => {
        if (open) {
            // Check permissions
            const canManage = spaceData.user_permission?.includes(PERMISSIONS.MANAGE_COLLECTIONS) || false;
            const canViewCollabs = spaceData.collaborators !== undefined;
            
            // If user can't manage space, show Collaborators tab (if available) or first available tab
            if (!canManage) {
                if (canViewCollabs) {
                    // Collaborators tab index depends on whether General tab exists
                    setTabValue(canManage ? 1 : 0);
                } else {
                    // No tabs available for this user
                    setTabValue(0);
                }
            } else if (tabValue >= 3) {
                // If tab index is out of bounds, reset to first tab
                setTabValue(0);
            }
        }
    }, [open, spaceData.user_permission, spaceData.collaborators]);
    const [collaboratorEmail, setCollaboratorEmail] = useState("");
    const [selectedPermissions, setSelectedPermissions] = useState([PERMISSIONS.VIEW_ONLY]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [collaboratorToDelete, setCollaboratorToDelete] = useState(null);
    const [isSpacePublic, setIsSpacePublic] = useState(spaceData.is_public);
    const [shareWithCommunity, setShareWithCommunity] = useState(spaceData.is_community);
    const [displayFollowersCount, setDisplayFollowersCount] = useState(
        spaceData.display_followers_count !== undefined ? spaceData.display_followers_count : true
    );
    const [organizationVisibility, setOrganizationVisibility] = useState(
        spaceData.organization_visibility ?? 'everyone'
    );
    const [subSpacesDialogOpen, setSubSpacesDialogOpen] = useState(false);
    const [selectedSubSpaces, setSelectedSubSpaces] = useState([]);
    const [makeAllSubSpacesPublic, setMakeAllSubSpacesPublic] = useState(false);
    const [addCollaboratorDialog, setAddCollaboratorDialog] = useState({
        open: false,
        email: "",
        permissions: [PERMISSIONS.VIEW_ONLY],
        isLoading: false,
        error: null
    });
    const [editPermissionsDialog, setEditPermissionsDialog] = useState({
        open: false,
        collaborator: null,
        permissions: [],
        isLoading: false,
        error: null
    });
    
    // New state for recommendation settings - only initialize if field exists
    const [isRecommendationActive, setIsRecommendationActive] = useState(
        spaceData.search_for_recommendations !== undefined ? spaceData.search_for_recommendations : false
    );
    const [recommendationPrompt, setRecommendationPrompt] = useState(
        spaceData.background_context !== undefined 
            ? (spaceData.background_context || spaceData.recommendation_prompt || "") 
            : ""
    );
    const [isEditingBackgroundContext, setIsEditingBackgroundContext] = useState(false);
    const [originalBackgroundContext, setOriginalBackgroundContext] = useState(spaceData.background_context || spaceData.recommendation_prompt || "");
    
    // State for recommendation limits
    const [recommendationLimitData, setRecommendationLimitData] = useState({
        spaces: [],
        allow_recommendations_available: true,
        loading: false,
        error: null
    });

    const currentPageURL = typeof window !== "undefined" ? window.location.href : "http://example.com";

    // Fetch spaces with recommendations enabled
    const fetchRecommendationSpaces = async () => {
        setRecommendationLimitData(prev => ({ ...prev, loading: true, error: null }));
        try {
            const response = await axios.get(`${API_URL}/spaces/authenticated/spaces-with-search-recommendations`);
            const spacesWithRecommendations = response.data.spaces || [];
            const recommendationLimit = response.data.allow_recommendations_limit || 5;
            
            
            // Check if limit is reached (spaces count >= limit)
            const limitReached = spacesWithRecommendations.length >= recommendationLimit;
            
            setRecommendationLimitData({
                spaces: spacesWithRecommendations.map(space => ({
                    id: space.id,
                    name: space.name
                })),
                allow_recommendations_available: !limitReached,
                recommendationLimit: recommendationLimit,
                loading: false,
                error: null
            });
        } catch (error) {
            console.error('Error fetching recommendation spaces:', error);
            setRecommendationLimitData(prev => ({
                ...prev,
                loading: false,
                error: error.response?.data?.error || 'Failed to fetch recommendation data'
            }));
            
            // Show error popup
            dispatch({
                type: "SET_POPUP",
                payload: {
                    text: error.response?.data?.error || "Failed to fetch recommendation data",
                    type: "error",
                },
            });
        }
    };

    // Fetch recommendation data when dialog opens and tab changes to recommendations
    useEffect(() => {
        const canManage = spaceData.user_permission?.includes(PERMISSIONS.MANAGE_COLLECTIONS) || false;
        const canViewCollabs = spaceData.collaborators !== undefined;
        // Calculate the correct tab index for Recommendations tab
        const recommendationsTabIndex = canManage && canViewCollabs ? 2 : (canManage ? 1 : -1);
        
        if (open && canManage && tabValue === recommendationsTabIndex) {
            fetchRecommendationSpaces();
        }
    }, [open, tabValue, spaceData.user_permission, spaceData.collaborators]);

    // Sync state with spaceData when it changes or when dialog opens
    useEffect(() => {
        if (spaceData && open) {
            console.log('spaceData', spaceData.is_public);
            setIsSpacePublic(spaceData.is_public || false);
            setShareWithCommunity(spaceData.is_community || false);
            setDisplayFollowersCount(
                spaceData.display_followers_count !== undefined 
                    ? spaceData.display_followers_count 
                    : true
            );
            if (spaceData.organization_id && spaceData.organization_visibility !== undefined) {
                setOrganizationVisibility(spaceData.organization_visibility || 'everyone');
            }
        }
    }, [spaceData, open]);

    // Reset edit mode when dialog closes
    useEffect(() => {
        if (!open) {
            setIsEditingBackgroundContext(false);
            // Reset to original value when dialog closes
            const currentValue = spaceData.background_context || spaceData.recommendation_prompt || "";
            setRecommendationPrompt(currentValue);
            setOriginalBackgroundContext(currentValue);
        }
    }, [open, spaceData.background_context, spaceData.recommendation_prompt]);

    // Sync recommendationPrompt when spaceData changes - only if fields exist
    useEffect(() => {
        if (spaceData.background_context !== undefined) {
            const newValue = spaceData.background_context || spaceData.recommendation_prompt || "";
            setRecommendationPrompt(newValue);
            if (!isEditingBackgroundContext) {
                setOriginalBackgroundContext(newValue);
            }
        } else {
            // If field doesn't exist, clear the prompt
            setRecommendationPrompt("");
            if (!isEditingBackgroundContext) {
                setOriginalBackgroundContext("");
            }
        }
        if (spaceData.search_for_recommendations !== undefined) {
            setIsRecommendationActive(spaceData.search_for_recommendations || false);
        } else {
            setIsRecommendationActive(false);
        }
    }, [spaceData.background_context, spaceData.recommendation_prompt, spaceData.search_for_recommendations, isEditingBackgroundContext]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        // Fetch recommendation data when switching to recommendations tab
        const canManage = spaceData.user_permission?.includes(PERMISSIONS.MANAGE_COLLECTIONS) || false;
        const canViewCollabs = spaceData.collaborators !== undefined;
        const recommendationsTabIndex = canManage && canViewCollabs ? 2 : (canManage ? 1 : -1);
        
        if (canManage && newValue === recommendationsTabIndex) {
            fetchRecommendationSpaces();
        }
    };

    const handleDeleteClick = (collaborator) => {
        setCollaboratorToDelete(collaborator);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (collaboratorToDelete) {
            try {
                onDeleteCollaboratorClick (collaboratorToDelete);
                setDeleteConfirmOpen(false);
                setCollaboratorToDelete(null);
            } catch (error) {
                // Handle permission-related errors
                if (error.message.includes('permission')) {
                    dispatch({
                        type: "SET_POPUP",
                        payload: {
                            text: "You don't have permission to remove collaborators from this space",
                            type: "error",
                        },
                    });
                }
            }
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setCollaboratorToDelete(null);
    };

    const handlePublicChange = (e) => {
        const newIsPublic = e.target.checked;
        setIsSpacePublic(newIsPublic);
        
        if (newIsPublic && subSpaces.length > 0) {
            setSubSpacesDialogOpen(true);
        } else {
            onChangeSpace({ is_public: newIsPublic });
        }
    };

    const handleSubSpacesDialogClose = () => {
        setSubSpacesDialogOpen(false);
        setSelectedSubSpaces([]);
        setMakeAllSubSpacesPublic(false);
    };

    const handleSubSpacesDialogConfirm = () => {
        const updatedFields = { 
            is_public: true,
            sub_spaces_visibility: makeAllSubSpacesPublic ? 'all' : 'selected',
            selected_sub_spaces: makeAllSubSpacesPublic ? [] : selectedSubSpaces
        };
        onChangeSpace(updatedFields);
        setSubSpacesDialogOpen(false);
    };

    const handleSubSpaceToggle = (spaceId) => {
        setSelectedSubSpaces(prev => {
            if (prev.includes(spaceId)) {
                return prev.filter(id => id !== spaceId);
            } else {
                return [...prev, spaceId];
            }
        });
    };

    const handlePermissionChange = (event) => {
        const value = event.target.value;
        
        // Handle read-only exclusive selection
        if (value.includes(PERMISSIONS.VIEW_ONLY)) {
            setSelectedPermissions([PERMISSIONS.VIEW_ONLY]);
            return;
        }

        // Remove read-only if other permissions are selected
        const filteredValue = value.filter(p => p !== PERMISSIONS.VIEW_ONLY);
        setSelectedPermissions(filteredValue);
    };

    const handleAddCollaboratorClick = () => {
        setAddCollaboratorDialog({
            open: true,
            email: collaboratorEmail,
            permissions: [PERMISSIONS.VIEW_ONLY],
            isLoading: false,
            error: null
        });
        setCollaboratorEmail("");
    };

    const handleAddCollaboratorClose = () => {
        setAddCollaboratorDialog({
            open: false,
            email: "",
            permissions: [PERMISSIONS.VIEW_ONLY],
            isLoading: false,
            error: null
        });
    };

    const handleAddCollaboratorSave = async () => {
        try {
            setAddCollaboratorDialog(prev => ({ ...prev, isLoading: true, error: null }));
            
            // Just call onAddCollaboratorClick which will handle the API call
            onAddCollaboratorClick(addCollaboratorDialog);
            handleAddCollaboratorClose();
            
        } catch (error) {
            setAddCollaboratorDialog(prev => ({
                ...prev,
                isLoading: false,
                error: error.response?.data?.error || 'Failed to add collaborator'
            }));
        }
    };

    const handleAddPermissionChange = (event) => {
        const value = event.target.value;
        const clickedPermission = value[value.length - 1]; // Get the last clicked permission
        
        // Always set only the clicked permission (radio button behavior)
        setAddCollaboratorDialog(prev => ({
            ...prev,
            permissions: [clickedPermission]
        }));
    };

    const handleEditPermissionsOpen = (collaborator) => {
        setEditPermissionsDialog({
            open: true,
            collaborator,
            permissions: collaborator.permissions || [PERMISSIONS.VIEW_ONLY],
            isLoading: false,
            error: null
        });
    };

    const handleEditPermissionsClose = () => {
        setEditPermissionsDialog({
            open: false,
            collaborator: null,
            permissions: [],
            isLoading: false,
            error: null
        });
    };

    const handleEditPermissionsSave = async () => {
        try {
            setEditPermissionsDialog(prev => ({ ...prev, isLoading: true, error: null }));
            
            const response = await axios.put(`${API_URL}/spaces/${spaceData.id}/collaborators`, {
                collaborators: [{
                    email: editPermissionsDialog.collaborator.email,
                    permissions: editPermissionsDialog.permissions
                }]
            });

            handleEditPermissionsClose();
            // Refresh collaborators list
            if (onUpdateCollaboratorPermissions) {
                onUpdateCollaboratorPermissions(response.data);
            }
            // Call getSpace to refresh the data
            getSpace();
            
        } catch (error) {
            setEditPermissionsDialog(prev => ({
                ...prev,
                isLoading: false,
                error: error.response?.data?.error || 'Failed to update permissions'
            }));
        }
    };

    const handleEditPermissionsChange = (event) => {
        const value = event.target.value;
        const clickedPermission = value[value.length - 1]; // Get the last clicked permission
        
        // Always set only the clicked permission (radio button behavior)
        setEditPermissionsDialog(prev => ({
            ...prev,
            permissions: [clickedPermission]
        }));
    };

    const handleRecommendationToggle = async (e) => {
        const newIsActive = e.target.checked;
        
        // Check if trying to enable when limit is reached
        if (newIsActive && !recommendationLimitData.allow_recommendations_available) {
            dispatch({
                type: "SET_POPUP",
                payload: {
                    text: "You have reached the maximum limit of 5 spaces with recommendations enabled",
                    type: "error",
                },
            });
            return;
        }
        
        setIsRecommendationActive(newIsActive);
        
        const updatedFields = { 
            search_for_recommendations: newIsActive,
            background_context: newIsActive ? recommendationPrompt : "",
            recommendation_prompt: newIsActive ? recommendationPrompt : ""
        };
        
        try {
            await onChangeSpace(updatedFields);
            // Refresh recommendation data after successful update
            if (tabValue === 2) {
                fetchRecommendationSpaces();
            }
        } catch (error) {
            // Revert state if update failed
            setIsRecommendationActive(!newIsActive);
            dispatch({
                type: "SET_POPUP",
                payload: {
                    text: error.response?.data?.error || "Failed to update recommendation settings",
                    type: "error",
                },
            });
        }
    };

    const handleRecommendationPromptChange = (e) => {
        const newPrompt = e.target.value;
        setRecommendationPrompt(newPrompt);
    };

    const handleEditBackgroundContext = () => {
        // Store original value and enter edit mode
        setOriginalBackgroundContext(recommendationPrompt);
        setIsEditingBackgroundContext(true);
    };

    const handleSaveBackgroundContext = () => {
        // Save to backend when save button is clicked
        const updatedFields = { 
            background_context: recommendationPrompt,
            recommendation_prompt: recommendationPrompt
        };
        onChangeSpace(updatedFields);
        setIsEditingBackgroundContext(false);
    };

    const handleCancelBackgroundContext = () => {
        // Revert to original value and exit edit mode
        setRecommendationPrompt(originalBackgroundContext);
        setIsEditingBackgroundContext(false);
    };

    const hasPermission = (permission) => {
        return spaceData.user_permission?.includes(permission) || 
               spaceData.user_permission?.includes(PERMISSIONS.MANAGE_COLLECTIONS);
    };

    // Check if user is the space owner by comparing user_name
    const isSpaceOwner = user && spaceData.user_name && (spaceData.user_name === user.user_name);
    
    // Check permissions - use user_permission array from spaceData
    const canManageSpace = spaceData.user_permission?.includes(PERMISSIONS.MANAGE_COLLECTIONS) || false;
    const canAddItems = spaceData.user_permission?.includes(PERMISSIONS.ADD_ITEMS) || false;
    
    // Check if collaborators field exists (defensive check)
    const canViewCollaborators = spaceData.collaborators !== undefined;

    // Check if recommendations can be enabled
    const canEnableRecommendations = canManageSpace && (
        isRecommendationActive || recommendationLimitData.allow_recommendations_available
    );

    return (
        <>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={open}
                onClose={onClose}
                closeAfterTransition
            >
                <Fade in={open}>
                    <Box sx={dialogStyle}>
                        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                            Collection Settings
                        </Typography>
                        
                        <Divider sx={{ mb: 2 }} />

                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            aria-label="settings tabs"
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            {canManageSpace && (
                                <Tab 
                                    icon={<SettingsIcon />} 
                                    label="General" 
                                    iconPosition="start"
                                />
                            )}
                            {/* Collaborators tab - only show if user can view collaborators */}
                            {canViewCollaborators && (
                                <Tab 
                                    icon={<GroupIcon />} 
                                    label="Collaborators" 
                                    iconPosition="start"
                                />
                            )}
                            {canManageSpace && (
                                <Tab 
                                    icon={<RecommendIcon />} 
                                    label="Recommendation" 
                                    iconPosition="start"
                                />
                            )}
                        </Tabs>

                        {canManageSpace && (
                            <TabPanel value={tabValue} index={0}>
                                {spaceData.organization_id && (
                                    <Box mb={3}>
                                        <FormControl component="fieldset">
                                            <FormLabel component="legend" sx={{ mb: 1 }}>Organization visibility</FormLabel>
                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                Who in your organization can access this collection
                                            </Typography>
                                            <RadioGroup
                                                value={organizationVisibility}
                                                onChange={(e) => {
                                                    const newVal = e.target.value;
                                                    setOrganizationVisibility(newVal);
                                                    onChangeSpace({ organization_visibility: newVal });
                                                }}
                                                disabled={!canManageSpace}
                                            >
                                                <FormControlLabel
                                                    value="everyone"
                                                    control={<Radio />}
                                                    label="Everyone in organization"
                                                />
                                                <FormControlLabel
                                                    value="specific"
                                                    control={<Radio />}
                                                    label="Specific people only"
                                                />
                                            </RadioGroup>
                                            {organizationVisibility === 'specific' && (
                                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1, ml: 2 }}>
                                                    Use the Collaborators tab to add or remove people who can access this collection.
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Box>
                                )}
                                <Box mb={3}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={isSpacePublic}
                                                onChange={handlePublicChange}
                                                disabled={!canManageSpace}
                                            />
                                        }
                                        label="Make space public"
                                    />

                                    {isSpacePublic && (
                                        <Box ml={{ xs: 2, sm: 6 }} mt={2}>
                                            <Typography variant="body2" color="textSecondary">
                                                When the collection is public, anyone with the link can access it.
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                <strong>Space URL:</strong>
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={currentPageURL}
                                                InputProps={{ readOnly: true }}
                                                sx={{ mt: 1 }}
                                            />
                                        </Box>
                                    )}
                                </Box>

                                {isSpacePublic && (
                                    <Box mb={3}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={shareWithCommunity}
                                                    onChange={(e) => {
                                                        const updatedFields = { is_community: e.target.checked };
                                                        setShareWithCommunity(e.target.checked);
                                                        onChangeSpace(updatedFields);
                                                    }}
                                                    disabled={!isSpaceEmpty || !canManageSpace}
                                                />
                                            }
                                            label="Share with community"
                                        />
                                        {shareWithCommunity && (
                                            <Typography variant="body2" sx={{ ml: { xs: 2, sm: 6 } }}>
                                                This collection will be discoverable on the community page by everyone.
                                            </Typography>
                                        )}
                                        {!isSpaceEmpty && (
                                            <Typography variant="body2" sx={{ ml: { xs: 2, sm: 6 }, color: "gray" }}>
                                                (Disabled because the space is not empty)
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {isSpacePublic&& (
                                    <Box mb={3}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={displayFollowersCount}
                                                    onChange={(e) => {
                                                        const updatedFields = { display_followers_count: e.target.checked };
                                                        setDisplayFollowersCount(e.target.checked);
                                                        onChangeSpace(updatedFields);
                                                    }}
                                                    disabled={!canManageSpace}
                                                />
                                            }
                                            label="Display follower count"
                                        />
                                        <Typography variant="body2" sx={{ ml: { xs: 2, sm: 6 }, mt: 1 }}>
                                            {displayFollowersCount 
                                                ? "The number of followers will be visible to everyone viewing this collection."
                                                : "The follower count will be hidden from public view."}
                                        </Typography>
                                    </Box>
                                )}

                                <Box textAlign={{ xs: "center", sm: "left" }}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={() => onDeleteSpaceClick?.()}
                                        disabled={!canManageSpace}
                                    >
                                        Delete Space
                                    </Button>
                                </Box>
                            </TabPanel>
                        )}

                        {canViewCollaborators && (
                            <TabPanel value={tabValue} index={canManageSpace ? 1 : 0}>
                                <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                                    {canManageSpace 
                                        ? "Manage collaborator access and permissions for this collection."
                                        : "View collaborators for this collection."
                                    }
                                </Typography>

                                {canManageSpace && (
                                <Box display="flex" flexDirection="column" gap={2} mb={3}>
                                    <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                                        <Box flex={1}>
                                            <Input
                                                fullWidth
                                                value={collaboratorEmail}
                                                onChange={(e) => setCollaboratorEmail(e.target.value)}
                                                placeholder="Enter collaborator's email"
                                                disabled={!canManageSpace}
                                            />
                                        </Box>
                                        <Button
                                            disabled={!collaboratorEmail || !canManageSpace}
                                            onClick={handleAddCollaboratorClick}
                                            variant="contained"
                                        >
                                            Add Collaborator
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            <TableContainer
                                component={Paper}
                                sx={{
                                    maxHeight: "400px",
                                    overflowY: "auto",
                                }}
                            >
                                <Table
                                    sx={{ minWidth: 250 }}
                                    stickyHeader
                                    aria-label="collaborators table"
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left">Name</TableCell>
                                            <TableCell align="left">Email</TableCell>
                                            <TableCell align="left">Status</TableCell>
                                            <TableCell align="left">Permissions</TableCell>
                                            {isSpaceOwner && (
                                                <TableCell align="left">Actions</TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {collaborators.map((c) => (
                                            <TableRow key={c.email}>
                                                <TableCell align="left">{c.name || 'Pending User'}</TableCell>
                                                <TableCell align="left">{c.email}</TableCell>
                                                <TableCell align="left">
                                                    <Chip
                                                        icon={STATUS_CONFIGS[c.status || COLLABORATOR_STATUS.PENDING].icon}
                                                        label={STATUS_CONFIGS[c.status || COLLABORATOR_STATUS.PENDING].label}
                                                        size="small"
                                                        color={STATUS_CONFIGS[c.status || COLLABORATOR_STATUS.PENDING].color}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="left">
                                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                                        {(c.permissions || [PERMISSIONS.VIEW_ONLY]).map((permission) => (
                                                            <Tooltip key={permission} title={PERMISSION_CONFIGS[permission].description}>
                                                                <Chip
                                                                    label={PERMISSION_CONFIGS[permission].label}
                                                                    size="small"
                                                                    color={PERMISSION_CONFIGS[permission].color}
                                                                    variant="outlined"
                                                                />
                                                            </Tooltip>
                                                        ))}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="left">
                                                    {isSpaceOwner && (
                                                        <Box display="flex" gap={1}>
                                                            <Tooltip title="Edit Permissions">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleEditPermissionsOpen(c)}
                                                                    sx={{ color: blue[500] }}
                                                                    disabled={c.status === COLLABORATOR_STATUS.PENDING}
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Remove Collaborator">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteClick(c)}
                                                                    sx={{ color: pink[500] }}
                                                                >
                                                                    <HighlightOff />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                    </TableContainer>
                            </TabPanel>
                        )}

                        {canManageSpace && (
                            <TabPanel value={tabValue} index={canViewCollaborators ? 2 : 1}>
                            <Box mb={3}>
                                <Typography variant="h6" gutterBottom>
                                    AI Recommendation Settings
                                </Typography>
                                
                                {/* Loading state */}
                                {recommendationLimitData.loading && (
                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <CircularProgress size={20} />
                                        <Typography variant="body2">
                                            Loading recommendation data...
                                        </Typography>
                                    </Box>
                                )}

                                {/* Error state */}
                                {recommendationLimitData.error && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {recommendationLimitData.error}
                                    </Alert>
                                )}

                                {/* Current status info */}
                                {!recommendationLimitData.loading && !recommendationLimitData.allow_recommendations_available && (
                                    <Box mb={2}>
                                        <Alert 
                                            severity="warning"
                                            icon={<InfoIcon />}
                                        >
                                            <Typography variant="body2">
                                                <strong>Current Status:</strong> {recommendationLimitData.spaces.length} of {recommendationLimitData.recommendationLimit} spaces have recommendations enabled. You have reached the maximum limit.
                                            </Typography>
                                        </Alert>
                                    </Box>
                                )}

                                {/* Spaces with recommendations enabled */}
                                {!recommendationLimitData.loading && !recommendationLimitData.allow_recommendations_available && recommendationLimitData.spaces.length > 0 && (
                                    <Box mb={3}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Spaces with Recommendations Enabled:
                                        </Typography>
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            {recommendationLimitData.spaces.map((space) => (
                                                <Chip
                                                    key={space.id}
                                                    label={space.name}
                                                    size="small"
                                                    color={space.id === spaceData.id ? "primary" : "default"}
                                                    variant={space.id === spaceData.id ? "filled" : "outlined"}
                                                    onClick={() => window.location.href = `/spaces/${space.id}`}
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: space.id === spaceData.id 
                                                                ? 'primary.dark' 
                                                                : 'action.hover'
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isRecommendationActive}
                                            onChange={handleRecommendationToggle}
                                            disabled={!canEnableRecommendations || recommendationLimitData.loading}
                                        />
                                    }
                                    label="Enable search for recommendations"
                                />

                                {/* Limit reached message */}
                                {!recommendationLimitData.allow_recommendations_available && !isRecommendationActive && (
                                    <Typography variant="body2" color="error" sx={{ ml: { xs: 2, sm: 6 }, mt: 1 }}>
                                        Cannot enable recommendations: You have reached the maximum limit of {recommendationLimitData.recommendationLimit} spaces. 
                                        Disable recommendations on another space first.
                                    </Typography>
                                )}

                                {isRecommendationActive && (
                                    <Box ml={{ xs: 2, sm: 6 }} mt={2}>
                                        <Typography variant="body2" color="textSecondary" paragraph>
                                            When enabled, AI will search for relevant recommendations based on your collection content.
                                        </Typography>
                                        
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            <Typography variant="body2">
                                                <strong>Disclaimer:</strong> When recommendations are active, we search online using external AI tools and search engines to find relevant content based on your collection. However, we do not establish direct connections between external data sources and your client data. All recommendations are provided for informational purposes only.
                                            </Typography>
                                        </Alert>
                                    </Box>
                                )}

                                {/* Background Context - Always visible and editable */}
                                <Box ml={{ xs: 2, sm: 6 }} mt={2}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                                            Background Context (Optional)
                                        </Typography>
                                        {!isEditingBackgroundContext ? (
                                            <Tooltip title="Edit Background Context">
                                                <IconButton
                                                    onClick={handleEditBackgroundContext}
                                                    disabled={!canManageSpace}
                                                    color="primary"
                                                    size="small"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <>
                                                <Tooltip title="Save Background Context">
                                                    <IconButton
                                                        onClick={handleSaveBackgroundContext}
                                                        disabled={!canManageSpace}
                                                        color="primary"
                                                        size="small"
                                                    >
                                                        <SaveIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Cancel">
                                                    <IconButton
                                                        onClick={handleCancelBackgroundContext}
                                                        disabled={!canManageSpace}
                                                        color="default"
                                                        size="small"
                                                    >
                                                        <CancelIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" paragraph>
                                        Provide additional context or prompts to help AI better understand your collection and provide more relevant recommendations.
                                    </Typography>
                                    
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        value={recommendationPrompt}
                                        onChange={handleRecommendationPromptChange}
                                        placeholder="e.g., This collection focuses on machine learning research papers, particularly in the areas of computer vision and natural language processing..."
                                        variant="outlined"
                                        size="small"
                                        disabled={!canManageSpace || !isEditingBackgroundContext}
                                        sx={{ mt: 1 }}
                                    />
                                </Box>
                            </Box>
                            </TabPanel>
                        )}
                    </Box>
                </Fade>
            </Modal>

            {/* Sub-spaces visibility dialog */}
            <Dialog
                open={subSpacesDialogOpen}
                onClose={handleSubSpacesDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Choose Sub-collections Visibility</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={makeAllSubSpacesPublic}
                                    onChange={(e) => setMakeAllSubSpacesPublic(e.target.checked)}
                                />
                            }
                            label="Make all sub-collections public"
                        />
                    </Box>
                    
                    {!makeAllSubSpacesPublic && (
                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Select specific sub-collections to make public:
                            </Typography>
                            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                                {subSpaces.map((space) => (
                                    <ListItem
                                        key={space.id}
                                        button
                                        onClick={() => handleSubSpaceToggle(space.id)}
                                    >
                                        <ListItemIcon>
                                            <Checkbox
                                                edge="start"
                                                checked={selectedSubSpaces.includes(space.id)}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                        </ListItemIcon>
                                        <ListItemText primary={space.name} />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSubSpacesDialogClose}>Cancel</Button>
                    <Button 
                        onClick={handleSubSpacesDialogConfirm}
                        variant="contained"
                        disabled={!makeAllSubSpacesPublic && selectedSubSpaces.length === 0}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Collaborator Dialog */}
            <Dialog
                open={addCollaboratorDialog.open}
                onClose={handleAddCollaboratorClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Add New Collaborator
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 3, mt: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Adding: <strong>{addCollaboratorDialog.email}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Select the permissions you want to grant to this collaborator. You can modify these permissions later.
                        </Typography>
                        {addCollaboratorDialog.error && (
                            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                                {addCollaboratorDialog.error}
                            </Typography>
                        )}
                    </Box>
                    <FormControl fullWidth>
                        <InputLabel id="add-permissions-label">Permissions</InputLabel>
                        <Select
                            labelId="add-permissions-label"
                            multiple
                            value={addCollaboratorDialog.permissions}
                            onChange={handleAddPermissionChange}
                            input={<OutlinedInput label="Permissions" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={PERMISSION_CONFIGS[value].label}
                                            size="small"
                                            color={PERMISSION_CONFIGS[value].color}
                                        />
                                    ))}
                                </Box>
                            )}
                        >
                            {Object.entries(PERMISSION_CONFIGS).map(([value, config]) => (
                                <MenuItem 
                                    key={value} 
                                    value={value}
                                >
                                    <Checkbox checked={addCollaboratorDialog.permissions.includes(value)} />
                                    <ListItemText 
                                        primary={config.label}
                                        secondary={config.description}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleAddCollaboratorClose}
                        disabled={addCollaboratorDialog.isLoading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleAddCollaboratorSave}
                        variant="contained"
                        disabled={addCollaboratorDialog.permissions.length === 0 || addCollaboratorDialog.isLoading}
                    >
                        {addCollaboratorDialog.isLoading ? 'Sending...' : 'Send Invitation'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Permissions Dialog */}
            <Dialog
                open={editPermissionsDialog.open}
                onClose={handleEditPermissionsClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Edit Permissions for {editPermissionsDialog.collaborator?.name || editPermissionsDialog.collaborator?.email}
                </DialogTitle>
                <DialogContent>
                    {editPermissionsDialog.error && (
                        <Typography color="error" variant="body2" sx={{ mt: 1, mb: 2 }}>
                            {editPermissionsDialog.error}
                        </Typography>
                    )}
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel id="edit-permissions-label">Permissions</InputLabel>
                        <Select
                            labelId="edit-permissions-label"
                            multiple
                            value={editPermissionsDialog.permissions}
                            onChange={handleEditPermissionsChange}
                            input={<OutlinedInput label="Permissions" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={PERMISSION_CONFIGS[value].label}
                                            size="small"
                                            color={PERMISSION_CONFIGS[value].color}
                                        />
                                    ))}
                                </Box>
                            )}
                        >
                            {Object.entries(PERMISSION_CONFIGS).map(([value, config]) => (
                                <MenuItem 
                                    key={value} 
                                    value={value}
                                >
                                    <Checkbox checked={editPermissionsDialog.permissions.includes(value)} />
                                    <ListItemText 
                                        primary={config.label}
                                        secondary={config.description}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleEditPermissionsClose}
                        disabled={editPermissionsDialog.isLoading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleEditPermissionsSave}
                        variant="contained"
                        disabled={editPermissionsDialog.permissions.length === 0 || editPermissionsDialog.isLoading}
                    >
                        {editPermissionsDialog.isLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Remove Collaborator
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove {collaboratorToDelete?.name} ({collaboratorToDelete?.email}) from this collection?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SettingsDialog;
